import { WorksheetDomain } from '@mathflat/domain/@entities'
import type { CasAssignmentDtoT } from '@mathflat/domain/@entities/(Content)/Cas'
import { CONTENT_STATUS } from '@mathflat/domain/@entities/(Content)/module'
import { Entity as WorksheetEntity } from '@mathflat/domain/@entities/(Content)/Worksheet/dto'
import type { Entity as ProblemEntity } from '@mathflat/domain/@entities/Problem/dto'
import { Entity as StudentWorksheetEntity } from '@mathflat/domain/@entities/StudentWorksheet/dto'
import { Entity as StudentWorksheetScoringEntity } from '@mathflat/domain/@entities/StudentWorksheet/StudentWorksheetScoring/dto'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { casApi } from '~/@common/api/casApi'
import { commonRepo } from '~/@common/services/repo.service'
import {
  ProblemScoring,
  ProblemScoringColl,
} from '~/@pages/@common/(ProblemScoring)/@trait/ProblemScoring.trait'
import { ProblemScoringViewOption } from '~/@pages/@common/(ProblemScoring)/@trait/ProblemScoringViewOption.trait'

type Detail = CasAssignmentDtoT.AssignedCasDetail
type ScoringItem = CasAssignmentDtoT.AssignedCasScoringItem

// 자동시험 채점 결과를 학습지(WorksheetScoringByOne) 뷰에 그대로 흘려보내기 위한 어댑터.
// StudentExamService 와 동일한 패턴 — API 응답을 worksheet/studentWorksheet/problemScoringColl/viewOption 4종으로 매핑한다.
export class CasScoringService {
  private _problemScoringColl?: ProblemScoringColl<'WORKSHEET'>
  private _problemScoringViewOption?: ProblemScoringViewOption<'NOT_학습모듈'>
  private _studentWorksheet: StudentWorksheetEntity.StudentWorksheet | null = null
  private _worksheet?: WorksheetEntity.CustomWorksheet
  private _assignment: Detail | null = null

  get worksheet() {
    return this._worksheet
  }

  get studentWorksheet() {
    return this._studentWorksheet
  }

  get problemScoringColl() {
    return new ProblemScoringColl(this._problemScoringColl?.toArr ?? [])
  }

  get problemScoringViewOption() {
    return this._problemScoringViewOption
  }

  get assignment() {
    return this._assignment
  }

  constructor() {
    makeObservable<
      this,
      | '_problemScoringColl'
      | '_problemScoringViewOption'
      | '_studentWorksheet'
      | '_worksheet'
      | '_assignment'
    >(this, {
      _problemScoringColl: observable,
      _problemScoringViewOption: observable,
      _studentWorksheet: observable,
      _worksheet: observable,
      _assignment: observable,
      worksheet: computed,
      studentWorksheet: computed,
      problemScoringColl: computed,
      problemScoringViewOption: computed,
      assignment: computed,
      load: action.bound,
    })
  }

  async load(assignedId: number) {
    const [assignment, scoringList] = await Promise.all([
      casApi.getAssignment(assignedId),
      casApi.getScoringList(assignedId),
    ])

    // 자동시험은 출제·채점이 학습지 시험지 분기와 가깝지만, 학습지 ByOne 뷰가 type='시험'일 때 헤더 동작이 달라지므로 CUSTOM 으로 매핑한다.
    const worksheet = new WorksheetEntity.CustomWorksheet({
      id: assignment.assignedId,
      type: WorksheetDomain.TYPE.CUSTOM,
      school: assignment.schoolType,
      grade: assignment.grade,
      title: assignment.title,
      titleTag: 'BASIC',
      chapter: assignment.field ?? '',
      tag: 'BASIC',
      problemCount: assignment.maxProblemCount,
      level: 1,
      autoScorable: true,
      accessModifierToStudent: assignment.accessModifierToStudent,
    })

    const studentWorksheet = new StudentWorksheetEntity.StudentWorksheet({
      id: assignment.assignedId,
      assignDatetime: assignment.assignDatetime,
      openDatetime: assignment.startedAt,
      score: assignment.score ?? 0,
      scoreDatetime: assignment.completedAt,
      status: CONTENT_STATUS.전체채점,
      correctCount: assignment.correctCount,
      wrongCount: scoringList.length - assignment.correctCount,
    })

    const problemScorings = scoringList.map((item) =>
      buildProblemScoring(item, { school: assignment.schoolType, grade: assignment.grade }),
    )

    const viewOption = new ProblemScoringViewOption<'NOT_학습모듈'>({
      content: {
        worksheetId: assignment.assignedId,
        autoScored: true,
        status: CONTENT_STATUS.전체채점,
        kind: 'WORKSHEET',
        type: WorksheetDomain.TYPE.CUSTOM,
      },
      studentAppSetting: {
        일반채점_채점전정답공개: !!commonRepo.studentAppSetting?.일반채점_채점전정답공개,
        채점후정답해설공개: !!commonRepo.studentAppSetting?.채점후정답해설공개,
        채점전문풀동공개: !!commonRepo.studentAppSetting?.채점전문풀동공개,
        채점후문풀동공개: !!commonRepo.studentAppSetting?.채점후문풀동공개,
      },
    })

    runInAction(() => {
      this._assignment = assignment
      this._worksheet = worksheet
      this._studentWorksheet = studentWorksheet
      this._problemScoringColl = new ProblemScoringColl<'WORKSHEET'>(problemScorings)
      this._problemScoringViewOption = viewOption
    })
  }
}

function buildProblemScoring(
  item: ScoringItem,
  context: {
    school: Detail['schoolType']
    grade: Detail['grade']
  },
): ProblemScoring<'WORKSHEET'> {
  const problem = item.problem as unknown as ProblemEntity.Problem

  const scoring = new StudentWorksheetScoringEntity.StudentWorksheetScoring<'일반'>({
    scoring: {
      worksheetProblemId: item.scoringId,
      userAnswer: item.userAnswer ?? null,
      result: item.result,
    },
    problem,
    worksheet: {
      autoScorable: true,
      label: `${item.orderingNumber}번`,
      school: context.school,
      grade: context.grade,
    },
  })

  return new ProblemScoring<'WORKSHEET'>(
    {
      scoring,
      scoringSaveStorage: 'SERVER',
      handwrittenNoteSaveStorage: 'SERVER',
    },
    problem.id,
    item.handwrittenNoteUrl ?? null,
  )
}
